import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Divider
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon
} from '@mui/icons-material';
import axios from 'axios';
import { CONFIG } from '../../config/config';
import { getToken } from '../../services/auth';
import type { RaidItemResponse } from '../../services/api';
import { formatDate } from '../../utils/dateUtils';
import SystemGeneratedRaidIndicator from '../../components/SystemGeneratedRaidIndicator';

interface RaidHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  reviewQuestionAnswerId: number;
  title: string;
}

const RaidHistoryDialog: React.FC<RaidHistoryDialogProps> = ({
  open,
  onClose,
  reviewQuestionAnswerId,
  title
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [raidItems, setRaidItems] = useState<RaidItemResponse[]>([]);
  const [openRows, setOpenRows] = useState<{ [key: number]: boolean }>({});

  useEffect(() => {
    if (open && reviewQuestionAnswerId) {
      fetchRaidItemsHistory();
    }
    // Reset the state when the dialog closes
    return () => {
      if (!open) {
        setRaidItems([]);
        setOpenRows({});
        setError(null);
      }
    };
  }, [open, reviewQuestionAnswerId]);

  const fetchRaidItemsHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use axios with the proper base URL and authorization to fetch RAID items across all questionnaires
      const token = getToken();
      const response = await axios.get(
        `${CONFIG.SERVER_URL}/raid_items_by_review_question_answer_all_questionnaires`, {
          params: {
            review_question_answer_id: reviewQuestionAnswerId
          },
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      setRaidItems(response.data);
    } catch (error) {
      console.error('Error fetching RAID items history:', error);
      // Type check and cast the error to access its properties safely
      let errorMessage = 'Failed to fetch RAID items history';
      
      if (axios.isAxiosError(error)) {
        // Now TypeScript knows this is an AxiosError
        errorMessage = error.response?.data?.detail || error.message || errorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRowToggle = (raidId: number) => {
    setOpenRows(prev => ({
      ...prev,
      [raidId]: !prev[raidId]
    }));
  };

  const getStatusColor = (status: string) => {
    if (status.toLowerCase().includes('mitigated') || 
        status.toLowerCase().includes('resolved') || 
        status.toLowerCase().includes('fulfilled') || 
        status.toLowerCase().includes('validated') ||
        status.toLowerCase().includes('closed')) {
      return 'success';
    } else if (status.toLowerCase().includes('open')) {
      return 'warning';
    } else if (status.toLowerCase().includes('overdue')) {
      return 'error';
    }
    return 'default';
  };

  const getCategoryChipColor = (category: string) => {
    switch(category.toUpperCase()) {
      case 'RISK': return 'error';
      case 'ASSUMPTION': return 'info';
      case 'ISSUE': return 'warning';
      case 'DEPENDENCY': return 'secondary';
      case 'ACTION': return 'success';
      default: return 'default';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { 
          minHeight: '60vh',
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h6">
          RAID History - {title}
        </Typography>
        <Typography variant="subtitle2" color="text.secondary">
          Showing all RAID items across questionnaires for this question
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
        ) : raidItems.length === 0 ? (
          <Typography variant="body1" sx={{ p: 2 }}>
            No RAID items found for this question across questionnaires.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>System Generated</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Review Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Latest Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Created On</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Last Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {raidItems.map((item) => {
                  const latestDetail = item.details && item.details.length > 0 
                    ? item.details.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0] 
                    : null;
                  
                  return (
                    <React.Fragment key={item.raid_id}>
                      <TableRow 
                        hover
                        sx={{ '&:hover': { bgcolor: 'action.hover' }, cursor: 'pointer' }}
                        onClick={() => handleRowToggle(item.raid_id)}
                      >
                        <TableCell>
                          <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowToggle(item.raid_id);
                            }}
                          >
                            {openRows[item.raid_id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {item.title}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={item.raid_category} 
                            size="small" 
                            color={getCategoryChipColor(item.raid_category) as any}
                          />
                        </TableCell>
                        <TableCell>
                          <SystemGeneratedRaidIndicator isSystemGenerated={item.is_system_generated} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                            {item.review_name || 'N/A'}
                          </Typography>
                          {item.review_date && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                              {formatDate(item.review_date)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {latestDetail && (
                            <Chip 
                              label={latestDetail.status} 
                              size="small" 
                              color={getStatusColor(latestDetail.status) as any}
                            />
                          )}
                        </TableCell>
                        <TableCell>{formatDate(item.created_at)}</TableCell>
                        <TableCell>{formatDate(item.updated_at)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                          <Collapse in={openRows[item.raid_id]} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2 }}>
                              <Typography variant="h6" gutterBottom component="div">
                                Details History ({item.details.length})
                              </Typography>
                              <Divider sx={{ mb: 2 }} />
                              <TableContainer component={Paper} variant="outlined">
                                <Table size="small" aria-label="details">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold' }}>Content</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {item.details
                                      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                                      .map((detail, index) => (
                                        <TableRow key={detail.detail_id}>
                                          <TableCell component="th" scope="row">
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                              {formatDate(detail.created_at)}
                                              {index === 0 && (
                                                <Chip 
                                                  label="Latest" 
                                                  size="small" 
                                                  color="primary" 
                                                  sx={{ ml: 1 }}
                                                />
                                              )}
                                            </Box>
                                          </TableCell>
                                          <TableCell>
                                            <Chip 
                                              label={detail.status} 
                                              size="small" 
                                              color={getStatusColor(detail.status) as any}
                                            />
                                          </TableCell>
                                          <TableCell>
                                            <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
                                              <pre style={{ 
                                                margin: 0, 
                                                fontFamily: 'inherit', 
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word' 
                                              }}>
                                                {JSON.stringify(detail.content, null, 2)}
                                              </pre>
                                            </Box>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RaidHistoryDialog;
